'use client'

import type { ReactNode } from 'react'
import { useEffect } from 'react'
import { usePathname, useRouter } from 'next/navigation'
import { RouteSpinner } from '@/components/ui/route-spinner'
import { useSessionMeta, updateAppSessionState } from '@/state/session/context'

interface AuthGuardProps {
  children: ReactNode
}

export function AuthGuard({ children }: AuthGuardProps): React.JSX.Element {
  const router = useRouter()
  const pathname = usePathname()
  const {
    hydrated,
    isLoading,
    isAuthenticated,
    firstAccess,
    onboardingCompleted,
    postOnboardingRedirect,
  } = useSessionMeta()
  const onboardingRequired = isAuthenticated && (firstAccess || !onboardingCompleted)
  const shouldFollowPostOnboardingRedirect =
    isAuthenticated &&
    !isLoading &&
    !onboardingRequired &&
    Boolean(postOnboardingRedirect) &&
    postOnboardingRedirect !== pathname

  useEffect(() => {
    if (!hydrated || isLoading) return

    if (!isAuthenticated) {
      router.replace('/login')
      return
    }

    if (onboardingRequired) {
      router.replace('/onboarding/modules')
      return
    }

    if (postOnboardingRedirect) {
      const target = postOnboardingRedirect
      updateAppSessionState((prev) => ({
        ...prev,
        postOnboardingRedirect: null,
      }))

      if (target !== pathname) {
        router.replace(target)
      }
    }
  }, [
    hydrated,
    onboardingRequired,
    pathname,
    router,
    firstAccess,
    isAuthenticated,
    isLoading,
    onboardingCompleted,
    postOnboardingRedirect,
  ])

  if (
    !hydrated ||
    isLoading ||
    (!isAuthenticated) ||
    onboardingRequired ||
    shouldFollowPostOnboardingRedirect
  ) {
    return (
      <div className="mx-auto w-full max-w-6xl px-4 py-8">
        <RouteSpinner className="min-h-[320px]" label="INICIANDO PROTOCOLO..." showLabel />
      </div>
    )
  }

  return <>{children}</>
}
